/**
 * Table-of-Contents registry for the Progress Note.
 *
 * Order and labels are static; *presence* is not. Most sections and
 * subsections hide themselves when they have no content in read mode, so a
 * fixed link list would point at missing anchors on a short note. Each heading
 * registers its own id via `<NoteHeading>`, and the ToC renders the static
 * list filtered by what actually mounted — no duplicated visibility rules, and
 * no DOM scan racing the locked viewer's hydration.
 *
 * The provider component lives in `TocPresenceProvider.tsx`; fast refresh
 * requires a file to export either components or plain values, not both.
 */
import { createContext, useContext, useLayoutEffect, useMemo } from 'react'

export type TocLevel = 'section' | 'sub'

export type TocEntry = {
  id: string
  label: string
  level: TocLevel
}

/** Document order. Subsections follow the section they belong to. */
export const PROGRESS_NOTE_TOC_ENTRIES: TocEntry[] = [
  { id: 'encounter', label: 'Encounter Details', level: 'section' },

  { id: 'subjective', label: 'Subjective', level: 'section' },
  { id: 'consent', label: 'Consent', level: 'sub' },
  { id: 'chief-complaint', label: 'Chief Complaint', level: 'sub' },
  { id: 'hpi', label: 'History of Present Illness', level: 'sub' },
  { id: 'review-of-systems', label: 'Review of Systems', level: 'sub' },

  { id: 'objective', label: 'Objective', level: 'section' },
  { id: 'vitals', label: 'Vitals', level: 'sub' },
  { id: 'physical-exam', label: 'Physical Exam', level: 'sub' },
  { id: 'allergies', label: 'Allergies', level: 'sub' },

  { id: 'assessment', label: 'Assessment', level: 'section' },
  { id: 'clinical-impression', label: 'Clinical Impression', level: 'sub' },
  { id: 'problem-list', label: 'Problem List', level: 'sub' },
  { id: 'past-medical-history', label: 'Past Medical History', level: 'sub' },
  { id: 'family-history', label: 'Family History', level: 'sub' },

  { id: 'plan', label: 'Plan', level: 'section' },
  { id: 'plan-narrative', label: 'Plan Narrative', level: 'sub' },
  { id: 'orders', label: 'Orders', level: 'sub' },
  { id: 'medications', label: 'Medications', level: 'sub' },
  { id: 'billing-coding', label: 'Billing / Coding', level: 'section' },

  { id: 'mdm', label: 'Medical Decision Making', level: 'section' },

  { id: 'total-time', label: 'Total Time', level: 'section' },
  { id: 'care-coordinator-time', label: 'Care-Coordinator Time', level: 'sub' },

  { id: 'followup', label: 'Follow-Up', level: 'section' },
  { id: 'ai-disclosure', label: 'AI Documentation Disclosure', level: 'section' },
  { id: 'signature', label: 'Electronic Signature', level: 'section' },

  { id: 'addenda', label: 'Addendum', level: 'section' },
]

export type TocActions = {
  register: (id: string) => void
  unregister: (id: string) => void
}

// Split so the headings subscribe only to the stable actions: a combined
// context would hand them a new value on every registration, re-running the
// registering effect that produced it.
export const TocActionsContext = createContext<TocActions | undefined>(undefined)
export const TocPresenceContext = createContext<ReadonlySet<string> | undefined>(undefined)

/** Present entries in document order. Empty outside a provider. */
export function useTocEntries(): TocEntry[] {
  const present = useContext(TocPresenceContext)
  return useMemo(
    () => (present ? PROGRESS_NOTE_TOC_ENTRIES.filter((entry) => present.has(entry.id)) : []),
    [present],
  )
}

/**
 * Reports this heading's anchor to the ToC for as long as it is mounted.
 * `useLayoutEffect` so the ToC settles in the same paint the headings do.
 */
export function useTocAnchor(id: string): void {
  const actions = useContext(TocActionsContext)

  useLayoutEffect(() => {
    if (!actions) return
    actions.register(id)
    return () => actions.unregister(id)
  }, [id, actions])
}
